import catalogue from './colourPaletteCatalog.json';
import families from './colourPaletteFamilies.json';
import { seasonPalette } from './seasonPalette';

const FAMILY_KEYS = Object.keys(families);
const FAMILY_BY_NAME = new Map(FAMILY_KEYS.flatMap((family) =>
  families[family].map((name) => [name.toLowerCase(), family])));
const ALL_NAMES = Object.keys(catalogue);
const ALIASES = {
  'Fuchsia': ['fuschia'],
  'Hot pink': ['bright pink'],
  'Warm beige': ['yellowish beige'],
  'Soft grey': ['light grey', 'light gray'],
  'Espresso': ['espresso brown'],
};

function normalise(value) {
  return String(value || '').toLowerCase().normalize('NFKD')
    .replace(/\bgray\b/g, 'grey').replace(/[^a-z0-9]+/g, ' ').trim();
}

export function mentionsShade(answer, name) {
  const text = ` ${normalise(answer)} `;
  if (!text.trim()) return false;
  return [name, ...(ALIASES[name] || [])].some((candidate) =>
    text.includes(` ${normalise(candidate)} `));
}

function lab(hex) {
  const channels = [1, 3, 5].map((start) => parseInt(hex.slice(start, start + 2), 16) / 255)
    .map((value) => value <= 0.04045 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4);
  const [r, g, b] = channels;
  const x = (r * .4124564 + g * .3575761 + b * .1804375) / .95047;
  const y = r * .2126729 + g * .7151522 + b * .0721750;
  const z = (r * .0193339 + g * .1191920 + b * .9503041) / 1.08883;
  const curve = (value) => value > .008856 ? Math.cbrt(value) : (7.787 * value + 16 / 116);
  const fx = curve(x), fy = curve(y), fz = curve(z);
  return [116 * fy - 16, 500 * (fx - fy), 200 * (fy - fz)];
}

const LAB = new Map(ALL_NAMES.map((name) => [name, lab(catalogue[name])]));
function distance(first, second) {
  const a = LAB.get(first), b = LAB.get(second);
  if (!a || !b) return 100;
  return Math.hypot(a[0] - b[0], a[1] - b[1], a[2] - b[2]);
}

function score(name, anchors, dimensions) {
  const [lightness, a, b] = LAB.get(name);
  const depth = Number(dimensions?.overall_depth?.score);
  const intensity = Number(dimensions?.colour_intensity?.score);
  // The model's twelve choices are the strongest anchors. Depth and intensity
  // only gently reorder neighbouring shades; they cannot override the season
  // guide or create a new, unvalidated colour.
  const anchorDistance = anchors.length ? Math.min(...anchors.map((anchor) => distance(name, anchor))) : 35;
  const depthAdjustment = Number.isFinite(depth) ? Math.abs(lightness - (82 - depth * 54)) * .13 : 0;
  const chroma = Math.hypot(a, b);
  const intensityAdjustment = Number.isFinite(intensity) ? Math.abs(chroma - (18 + intensity * 45)) * .10 : 0;
  return anchorDistance + depthAdjustment + intensityAdjustment;
}

function rankedCandidates(candidates, anchors, dimensions) {
  return candidates.map((name, index) => ({ name, index,
    score: score(name, anchors, dimensions), hex: catalogue[name] }))
    .sort((a, b) => a.score - b.score || a.index - b.index);
}

function selectDistinct(candidates, anchors, dimensions, count) {
  const ranked = rankedCandidates(candidates, anchors, dimensions);
  const chosen = [];
  while (chosen.length < count && ranked.length) {
    ranked.sort((a, b) => {
      const value = (item) => item.score + chosen.reduce((penalty, other) =>
        penalty + Math.max(0, 18 - distance(item.name, other.name)) * .7, 0);
      return value(a) - value(b) || a.index - b.index;
    });
    chosen.push(ranked.shift());
  }
  return chosen.map(({ name, hex }) => ({ name, hex }));
}

export function personalPalette(analysis, experience = {}) {
  const season = analysis?.seasonal_interpretation?.closest;
  const reference = seasonPalette(season);
  const rejected = ALL_NAMES.filter((name) => mentionsShade(experience.knownBad, name));
  const rejectedSet = new Set(rejected);
  const works = ALL_NAMES.filter((name) => mentionsShade(experience.knownGood, name) && !rejectedSet.has(name));
  const worksSet = new Set(works);
  const selected = new Set();
  const original = FAMILY_KEYS.flatMap((family) => analysis?.palette_direction?.[family] || []);
  const groups = FAMILY_KEYS.map((family) => {
    const initial = (analysis?.palette_direction?.[family] || [])
      .filter((name) => catalogue[name] && FAMILY_BY_NAME.get(name.toLowerCase()) === family);
    const kept = [...new Set(initial.filter((name) => !rejectedSet.has(name)))].slice(0, 4);
    const available = (name) => FAMILY_BY_NAME.get(name.toLowerCase()) === family
      && !selected.has(name) && !rejectedSet.has(name) && !kept.includes(name);
    const seasonCandidates = (reference?.best || []).map(({ name }) => name).filter(available);
    const fillFromSeason = selectDistinct(seasonCandidates, kept, analysis?.dimensions, 4 - kept.length);
    const fillFromCatalogue = selectDistinct(families[family].filter((name) => available(name)
      && !fillFromSeason.some((shade) => shade.name === name)),
    [...kept, ...fillFromSeason.map(({ name }) => name)], analysis?.dimensions,
    4 - kept.length - fillFromSeason.length);
    const fill = [...fillFromSeason, ...fillFromCatalogue];
    const shades = [...kept.slice(0, 4).map((name) => ({ name, hex: catalogue[name] })), ...fill];
    shades.forEach(({ name }) => selected.add(name));
    return { family, shades };
  });
  const more = [];
  for (const family of FAMILY_KEYS) {
    const anchors = groups.find((group) => group.family === family).shades.map(({ name }) => name);
    const seasonCandidates = (reference?.best || []).map(({ name }) => name)
      .filter((name) => FAMILY_BY_NAME.get(name.toLowerCase()) === family && !selected.has(name) && !rejectedSet.has(name));
    const knownWorks = works.filter((name) => FAMILY_BY_NAME.get(name.toLowerCase()) === family && !selected.has(name));
    const preferred = knownWorks.map((name) => ({ name, hex: catalogue[name], fromExperience: true }));
    const remaining = rankedCandidates(seasonCandidates.filter((name) => !knownWorks.includes(name)),
      [...anchors, ...knownWorks], analysis?.dimensions)
      .map(({ name, hex }) => ({ name, hex }));
    more.push(...preferred, ...remaining);
  }
  const care = (reference?.caution || []).filter(({ name }) => !worksSet.has(name) && !rejectedSet.has(name));
  return {
    groups, more, care, rejected,
    replaced: original.filter((name) => rejectedSet.has(name)),
    season,
  };
}

export function reportColourExperience(analysis, experience = {}) {
  const palette = personalPalette(analysis, experience);
  return {
    knownGood: String(experience.knownGood || '').trim(),
    knownBad: String(experience.knownBad || '').trim(),
    favourites: String(experience.favourites || '').trim(),
    temperaturePreference: ['warm', 'cool', 'both'].includes(experience.temperaturePreference)
      ? experience.temperaturePreference : 'unsure',
    displayPalette: Object.fromEntries(palette.groups.map(({ family, shades }) =>
      [family, shades.map(({ name }) => name)])),
  };
}
